/**
 * features/students/StudentSubjectsTab.tsx
 * ------------------------------------------------------------------
 * The single place to edit which subjects a pupil takes and which
 * teacher teaches each one. Lives as a tab on the Student Profile.
 *
 * Subjects are no longer chosen at admission (admissions staff can't
 * know allocation); they are set here once the pupil has a class.
 *
 * Per-subject teacher options are scoped to teachers who actually teach
 * that subject in the pupil's class (getTeachersForSubjectInClass), and
 * unambiguous picks auto-fill (resolveStudentSubjectTeachers) — the same
 * logic the old Add-student modal used, now reused rather than copied.
 *
 * Editable for academic staff; read-only for anyone else who can open
 * the profile. The `subjectTeachers` map written here is exactly what
 * drives subject-teacher reach (marks entry, teacher→parent messaging).
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useState } from 'react';
import { doc } from 'firebase/firestore';
import { BookOpen, Save, AlertCircle } from 'lucide-react';
import { db, updateDoc } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, Button } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  getTeachersForSubjectInClass,
  resolveStudentSubjectTeachers,
} from '@/lib/teacherLink';
import type { Student } from '@/types';

/** Roles allowed to edit a pupil's subjects/teachers. Single source of
 *  truth for the gate — widen here if class teachers/HODs should edit. */
const EDIT_ROLES = ['admin', 'systemAdmin', 'academicAdmin', 'headteacher', 'editor'];

type SaveStatus = 'saving' | 'success' | 'error';

interface Props {
  student: Student;
}

/** Stable signature of a subjects+teachers selection for dirty-checking. */
function signature(subs: string[], map: Record<string, string>): string {
  const clean: [string, string][] = [];
  for (const sid of [...subs].sort()) {
    if (map[sid]) clean.push([sid, map[sid]]);
  }
  return JSON.stringify([...subs].sort()) + '|' + JSON.stringify(clean);
}

export default function StudentSubjectsTab({ student }: Props) {
  const { subjects, teachers, classes } = useData();
  const { userData } = useAuth();

  const roles =
    userData?.roles && userData.roles.length
      ? userData.roles
      : userData?.role
        ? [userData.role]
        : [];
  const canEdit = roles.some((r) => EDIT_ROLES.includes(r));

  const classId = student.currentClass;
  const className = useMemo(
    () => classes.find((c) => c.id === classId)?.name ?? '',
    [classes, classId],
  );

  const [selected, setSelected] = useState<string[]>(student.subjects ?? []);
  const [teacherMap, setTeacherMap] = useState<Record<string, string>>(
    student.subjectTeachers ?? {},
  );
  const [busy, setBusy] = useState(false);
  const [save, setSave] = useState<{ show: boolean; status: SaveStatus; message: string }>({
    show: false,
    status: 'saving',
    message: '',
  });

  // Auto-fill unambiguous teacher picks when the enrolled subjects change,
  // preserving any explicit pick that is still valid. Mirrors the old
  // Add-student behaviour via the shared resolver.
  useEffect(() => {
    if (!canEdit || !classId) return;
    const { map } = resolveStudentSubjectTeachers(
      teachers,
      classId,
      className,
      selected,
      teacherMap,
    );
    if (JSON.stringify(map) !== JSON.stringify(teacherMap)) setTeacherMap(map);
    // teacherMap intentionally omitted to avoid a feedback loop (resolver
    // already folds the previous map in via `existing`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, classId, className, canEdit, teachers]);

  const subjectsSorted = useMemo(
    () => [...subjects].sort((a, b) => a.name.localeCompare(b.name)),
    [subjects],
  );
  const subjectName = (id: string) => subjects.find((s) => s.id === id)?.name ?? id;
  const teacherName = (id?: string) =>
    id ? (teachers.find((t) => t.id === id)?.displayName ?? '—') : '—';

  const original = useMemo(
    () => signature(student.subjects ?? [], student.subjectTeachers ?? {}),
    [student.subjects, student.subjectTeachers],
  );
  const dirty = signature(selected, teacherMap) !== original;
  const unsetCount = selected.filter((sid) => !teacherMap[sid]).length;

  function toggleSubject(id: string) {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  async function handleSave() {
    if (!canEdit || !dirty || busy) return;
    setBusy(true);
    setSave({ show: true, status: 'saving', message: 'Saving subjects…' });
    try {
      // Drop teacher picks for subjects no longer enrolled.
      const subjectTeachers: Record<string, string> = {};
      for (const sid of selected) if (teacherMap[sid]) subjectTeachers[sid] = teacherMap[sid];
      await updateDoc(doc(db, COLLECTIONS.students, student.id), {
        subjects: selected,
        subjectTeachers,
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.uid,
      });
      setSave({ show: true, status: 'success', message: 'Subjects updated.' });
    } catch (e) {
      setSave({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  /* ----------------------------- read-only ----------------------------- */
  if (!canEdit) {
    return (
      <Card>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <BookOpen size={18} className="text-primary" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Subjects</h3>
          </div>
          {selected.length === 0 ? (
            <p className="text-sm text-gray-500">No subjects recorded yet.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {selected.map((sid) => (
                <div key={sid} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-gray-800 dark:text-gray-100">{subjectName(sid)}</span>
                  <span className="text-gray-500">{teacherName(teacherMap[sid])}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    );
  }

  /* ----------------------------- editable ------------------------------ */
  return (
    <div className="space-y-5">
      {!classId && (
        <Card>
          <div className="p-4 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
            <AlertCircle size={16} />
            This pupil has no class yet. You can choose subjects, but subject teachers can only be
            assigned once a class is set (teachers are scoped to the class).
          </div>
        </Card>
      )}

      {/* Subjects taken */}
      <Card>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <BookOpen size={18} className="text-primary" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Subjects taken</h3>
            <span className="text-xs text-gray-400">({selected.length} selected)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {subjectsSorted.length === 0 && (
              <span className="text-sm text-gray-500">
                No subjects are set up for the school yet.
              </span>
            )}
            {subjectsSorted.map((s) => {
              const on = selected.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleSubject(s.id)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                    on
                      ? 'border-primary bg-primary text-white'
                      : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                  }`}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Subject teachers */}
      {selected.length > 0 && classId && (
        <Card>
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">Subject teachers</h3>
              {unsetCount > 0 && (
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  {unsetCount} subject{unsetCount === 1 ? '' : 's'} still need a teacher
                </span>
              )}
            </div>
            <div className="space-y-2">
              {selected.map((sid) => {
                const avail = getTeachersForSubjectInClass(teachers, sid, classId, className);
                const chosen = teacherMap[sid] ?? '';
                return (
                  <div key={sid} className="flex items-center gap-3">
                    <span className="text-sm w-40 shrink-0 truncate text-gray-800 dark:text-gray-100">
                      {subjectName(sid)}
                    </span>
                    <select
                      value={chosen}
                      onChange={(e) =>
                        setTeacherMap((m) => ({ ...m, [sid]: e.target.value }))
                      }
                      className={`flex-1 px-2 py-1.5 text-sm rounded-lg border bg-white dark:bg-gray-800 dark:text-gray-100 ${
                        chosen
                          ? 'border-gray-300 dark:border-gray-700'
                          : 'border-amber-400'
                      }`}
                    >
                      <option value="">
                        {avail.length === 0
                          ? '— no teacher assigned to this subject/class —'
                          : '— choose a teacher —'}
                      </option>
                      {avail.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      <div className="flex justify-end">
        <Button icon={<Save size={16} />} onClick={handleSave} disabled={!dirty} loading={busy}>
          Save subjects
        </Button>
      </div>

      <SaveConfirmation
        show={save.show}
        status={save.status}
        message={save.message}
        onDismiss={() => setSave((s) => ({ ...s, show: false }))}
      />
    </div>
  );
}
